(() => {
  const PENDING_SUBMISSION_KEY = 'mpac-shopify-pending-submit-v3';
  const CONFIRMED_SUBMISSION_KEY = 'mpac-shopify-confirmed-submit-v3';
  const MAX_FILE_BYTES = 20 * 1024 * 1024;
  const MAX_PIXEL_COUNT = 50 * 1000 * 1000;
  const MAX_SANITIZED_EDGE = 3600;
  const SANITIZED_FILE_NAME = 'photo-personnalisee.jpg';
  const ALLOWED_FILES = {
    jpg: { mimes: ['image/jpeg', 'image/jpg'], signature: 'jpeg' },
    jpeg: { mimes: ['image/jpeg', 'image/jpg'], signature: 'jpeg' },
    png: { mimes: ['image/png'], signature: 'png' },
    webp: { mimes: ['image/webp'], signature: 'webp' }
  };

  function fileExtension(name) {
    return String(name || '').split('.').pop()?.toLowerCase() || '';
  }

  function createSubmissionToken() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }

  function bytesToText(bytes, start, length) {
    return String.fromCharCode(...bytes.slice(start, start + length));
  }

  function signatureMatches(bytes, signature) {
    if (signature === 'jpeg') return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    if (signature === 'png') return [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => bytes[index] === value);
    if (signature === 'webp') return bytesToText(bytes, 0, 4) === 'RIFF' && bytesToText(bytes, 8, 4) === 'WEBP';
    return false;
  }

  function uint16BigEndian(bytes, offset) {
    return (bytes[offset] << 8) | bytes[offset + 1];
  }

  function uint16LittleEndian(bytes, offset) {
    return bytes[offset] | (bytes[offset + 1] << 8);
  }

  function uint24LittleEndian(bytes, offset) {
    return bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
  }

  function preflightDimensions(bytes, signature) {
    if (signature === 'png' && bytes.length >= 24) {
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    if (signature === 'jpeg') {
      const sizeMarkers = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
      let offset = 2;
      while (offset + 8 < bytes.length) {
        if (bytes[offset] !== 0xff) {
          offset += 1;
          continue;
        }
        while (bytes[offset] === 0xff) offset += 1;
        const marker = bytes[offset];
        offset += 1;
        if (marker === 0xd9 || marker === 0xda) break;
        if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue;
        if (offset + 1 >= bytes.length) break;
        const segmentLength = uint16BigEndian(bytes, offset);
        if (segmentLength < 2 || offset + segmentLength > bytes.length) break;
        if (sizeMarkers.has(marker) && segmentLength >= 7) {
          return { width: uint16BigEndian(bytes, offset + 5), height: uint16BigEndian(bytes, offset + 3) };
        }
        offset += segmentLength;
      }
      return null;
    }
    if (signature === 'webp' && bytes.length >= 30) {
      const chunkType = bytesToText(bytes, 12, 4);
      if (chunkType === 'VP8X') {
        return { width: uint24LittleEndian(bytes, 24) + 1, height: uint24LittleEndian(bytes, 27) + 1 };
      }
      if (chunkType === 'VP8L' && bytes[20] === 0x2f) {
        return {
          width: 1 + bytes[21] + ((bytes[22] & 0x3f) << 8),
          height: 1 + (bytes[22] >> 6) + (bytes[23] << 2) + ((bytes[24] & 0x0f) << 10)
        };
      }
      if (chunkType === 'VP8 ' && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) {
        return { width: uint16LittleEndian(bytes, 26) & 0x3fff, height: uint16LittleEndian(bytes, 28) & 0x3fff };
      }
    }
    return null;
  }

  function scaledDimensions(dimensions, maxEdge) {
    const longestEdge = Math.max(dimensions.width, dimensions.height);
    const scale = Math.min(1, maxEdge / longestEdge);
    return {
      width: Math.max(1, Math.round(dimensions.width * scale)),
      height: Math.max(1, Math.round(dimensions.height * scale))
    };
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Le navigateur n’a pas pu préparer cette photo.'));
      }, 'image/jpeg', 0.94);
    });
  }

  async function sanitizePhoto(file, dimensions) {
    if (typeof window.createImageBitmap !== 'function' || typeof window.DataTransfer !== 'function' || typeof window.File !== 'function') {
      throw new Error('Utilisez une version récente de Chrome, Safari, Firefox ou Edge pour envoyer votre photo.');
    }
    const target = scaledDimensions(dimensions, MAX_SANITIZED_EDGE);
    let bitmap;
    let canvas;
    try {
      bitmap = await window.createImageBitmap(file, {
        resizeWidth: target.width,
        resizeHeight: target.height,
        resizeQuality: 'high',
        imageOrientation: 'from-image'
      });
      if (!bitmap.width || !bitmap.height || Math.max(bitmap.width, bitmap.height) > MAX_SANITIZED_EDGE + 1) {
        throw new Error('Les dimensions de cette photo sont invalides.');
      }
      canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Le navigateur ne peut pas préparer cette photo.');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await canvasToBlob(canvas);
      if (blob.size > MAX_FILE_BYTES) throw new Error('La photo préparée dépasse 20 Mo. Choisissez une version plus légère.');
      return {
        file: new window.File([blob], SANITIZED_FILE_NAME, { type: 'image/jpeg', lastModified: Date.now() }),
        width: canvas.width,
        height: canvas.height
      };
    } catch (error) {
      if (error instanceof Error && error.message) throw error;
      throw new Error('Cette image ne peut pas être lue. Choisissez une photo JPG, PNG ou WEBP valide.');
    } finally {
      bitmap?.close?.();
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
    }
  }

  async function inspectPhoto(file, formatMessage) {
    if (!file || file.size === 0) throw new Error('Choisissez une photo.');
    if (file.size > MAX_FILE_BYTES) throw new Error('Cette photo dépasse 20 Mo. Choisissez une version plus légère.');
    const extension = fileExtension(file.name);
    const expected = ALLOWED_FILES[extension];
    if (!expected) throw new Error(formatMessage || 'Choisissez une photo JPG, PNG ou WEBP.');
    const mime = String(file.type || '').toLowerCase();
    if (mime && !expected.mimes.includes(mime)) throw new Error('Le type du fichier ne correspond pas à son extension.');
    const header = new Uint8Array(await file.slice(0, Math.min(file.size, 512 * 1024)).arrayBuffer());
    if (!signatureMatches(header, expected.signature)) throw new Error('Ce fichier ne semble pas être une image valide.');
    const dimensions = preflightDimensions(header, expected.signature);
    if (!dimensions?.width || !dimensions?.height) throw new Error('Les dimensions de cette image ne peuvent pas être vérifiées.');
    if (dimensions.width * dimensions.height > MAX_PIXEL_COUNT) throw new Error('Cette photo dépasse 50 mégapixels. Réduisez sa taille avant de la choisir.');
    return sanitizePhoto(file, dimensions);
  }

  function replaceInputFile(input, file) {
    const transfer = new window.DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    if (input.files?.length !== 1 || input.files[0].name !== SANITIZED_FILE_NAME) {
      throw new Error('Le navigateur n’a pas pu préparer la photo pour l’envoi.');
    }
  }

  function initQuickOrder(root) {
    const form = root.querySelector('.mpac-quick-order__form');
    if (!form) return;
    const photoInput = form.querySelector('[data-quick-photo]');
    const uploadZone = form.querySelector('[data-quick-upload-zone]');
    const uploadTitle = form.querySelector('[data-quick-upload-title]');
    const previewWrap = form.querySelector('[data-quick-preview-wrap]');
    const preview = form.querySelector('[data-quick-preview]');
    const status = form.querySelector('[data-quick-status]');
    const orientationInput = form.querySelector('[data-quick-orientation]');
    const orientationLabel = form.querySelector('[data-quick-orientation-label]');
    const consent = form.querySelector('[data-quick-consent]');
    const consentDependents = [...form.querySelectorAll('[data-quick-consent-dependent]')];
    const tokenInput = form.querySelector('[data-quick-token]');
    const submitButton = form.querySelector('[data-quick-submit]');
    const submitLabel = form.querySelector('[data-quick-submit-label]');
    const quantityInput = form.querySelector('[data-quick-quantity]');
    const productTotal = form.querySelector('[data-quick-product-total]');
    const deliveredTotal = form.querySelector('[data-quick-delivered-total]');
    const shippingLabel = form.querySelector('[data-quick-shipping-label]');
    const buttonTotal = form.querySelector('[data-quick-button-total]');

    function updateQuantityPrice() {
      if (!quantityInput) return;
      const quantity = Number(quantityInput.value);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) return;
      const subtotal = quantity * Number(quantityInput.dataset.unitCents);
      const shipping = subtotal >= Number(quantityInput.dataset.thresholdCents) ? 0 : Number(quantityInput.dataset.shippingCents);
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: quantityInput.dataset.currency || 'EUR' }).format(cents / 100);
      productTotal.textContent = money(subtotal);
      buttonTotal.textContent = money(subtotal);
      deliveredTotal.textContent = money(subtotal + shipping);
      shippingLabel.textContent = shipping === 0 ? quantityInput.dataset.freeLabel : `${money(shipping)} ${quantityInput.dataset.shippingLabel}`;
    }
    quantityInput?.addEventListener('input', updateQuantityPrice);
    updateQuantityPrice();
    let photoReady = false;
    let processing = false;
    let submitting = false;
    let previewUrl = '';
    let activeToken = '';

    function setStatus(message, isError = false) {
      status.textContent = message;
      status.classList.toggle('is-error', isError);
      uploadZone.classList.toggle('is-error', isError);
      photoInput.setAttribute('aria-invalid', isError ? 'true' : 'false');
    }

    function updateConsentFields() {
      consentDependents.forEach((field) => { field.disabled = !consent.checked; });
    }

    function updateSubmit() {
      updateConsentFields();
      const disabled = !photoReady || !consent.checked || processing || submitting;
      submitButton.disabled = disabled;
      submitButton.setAttribute('aria-disabled', String(disabled));
      submitButton.setAttribute('aria-busy', submitting ? 'true' : 'false');
    }

    function clearPreview() {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = '';
      preview.removeAttribute('src');
      previewWrap.hidden = true;
      photoReady = false;
      uploadTitle.textContent = 'Choisir ma photo';
    }

    async function handlePhoto() {
      clearPreview();
      setStatus('');
      const file = photoInput.files?.[0];
      if (!file) {
        updateSubmit();
        return;
      }
      processing = true;
      uploadZone.classList.add('is-loading');
      uploadTitle.textContent = 'Préparation de la photo…';
      updateSubmit();
      try {
        const result = await inspectPhoto(file, root.dataset.photoFormat);
        replaceInputFile(photoInput, result.file);
        previewUrl = URL.createObjectURL(result.file);
        preview.src = previewUrl;
        previewWrap.hidden = false;
        const orientation = result.height > result.width ? 'Portrait' : 'Paysage';
        orientationInput.value = orientation;
        orientationLabel.textContent = orientation.toLowerCase();
        photoReady = true;
        uploadTitle.textContent = 'Changer la photo';
        setStatus(root.dataset.photoReady || 'Photo prête.');
      } catch (error) {
        photoInput.value = '';
        clearPreview();
        setStatus(error instanceof Error ? error.message : 'Cette photo ne peut pas être utilisée.', true);
      } finally {
        processing = false;
        uploadZone.classList.remove('is-loading');
        updateSubmit();
      }
    }

    function markPending(token) {
      try {
        sessionStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify({ token, savedAt: Date.now() }));
      } catch (_) { /* Session storage is optional. */ }
    }

    function submissionWasConfirmed(token) {
      try {
        const confirmation = JSON.parse(sessionStorage.getItem(CONFIRMED_SUBMISSION_KEY) || 'null');
        return Boolean(token && confirmation?.token === token);
      } catch (_) {
        return false;
      }
    }

    consent.addEventListener('change', updateSubmit);
    photoInput.addEventListener('change', () => { void handlePhoto(); });
    form.addEventListener('submit', (event) => {
      updateConsentFields();
      if (!photoReady || processing || !consent.checked || !form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        if (!photoReady && !processing) setStatus('Choisissez une photo avant de continuer.', true);
        return;
      }
      submitting = true;
      activeToken = createSubmissionToken();
      tokenInput.value = activeToken;
      markPending(activeToken);
      submitLabel.textContent = 'Ajout au panier…';
      updateSubmit();
    });

    window.addEventListener('pageshow', () => {
      if (activeToken && submissionWasConfirmed(activeToken)) {
        form.reset();
        clearPreview();
        setStatus('');
        try { sessionStorage.removeItem(CONFIRMED_SUBMISSION_KEY); } catch (_) { /* Session storage is optional. */ }
      }
      submitting = false;
      activeToken = '';
      submitLabel.textContent = 'Ajouter au panier';
      updateQuantityPrice();
      updateSubmit();
    });

    updateSubmit();
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-quick-order]').forEach(initQuickOrder);
  });
})();

